import { useRouter } from 'next/router';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { AppShell } from '../components/AppShell';
import { PageLoader } from '../components/symphony/ui';
import { PageHeader } from '../components/symphony/PageHeader';
import { usePageLoadBi } from '../lib/use-page-load-bi';
import { useTranslation } from '../lib/i18n';
import { useWorkspaces } from '../lib/workspaces';
import { TASKS_PAGE } from '../lib/tasks-bi';
import { TasksBody } from '../components/symphony/tasks/TasksBody';
import { useMarkVisited } from '../lib/use-last-visited';

export default function TasksPage() {
  const { t } = useTranslation();
  const router = useRouter();
  usePageLoadBi(TASKS_PAGE, { trackBack: true });
  // Opening the screen is what clears its "N new" badge on the hub tiles — see `lib/last-visited.ts`.
  useMarkVisited('tasks');

  // `?workspace=<slug>` (carried by a workspace's "Agents at work" links, and by the list's own Space
  // picker) scopes the list to that workspace; `?from=agents` changes where "back" leads. Without a slug
  // this is the account-wide list. The slug is resolved to an id HERE and the picker writes it back from
  // the list — one direction each, so the two can't disagree about which Space is selected.
  const slug = typeof router.query.workspace === 'string' ? router.query.workspace : '';
  const from = router.query.from === 'agents' ? 'agents' : undefined;
  // `?agent=<id>` is what an agent's own Tasks tile opens — a SEED for the list's agent picker, not a
  // fixed axis: the drill-in is a starting position the reader can widen back out of, the same rule the
  // Files browser's scope chip follows. The picker itself stays the source of truth after that, so the
  // id is not written back on every change and a stale one clamps away against the tasks in scope.
  const agentId = typeof router.query.agent === 'string' ? router.query.agent : '';
  const workspaces = useWorkspaces();
  const workspace = workspaces.status === 'ready' && slug ? workspaces.workspaces.find((w) => w.slug === slug) : undefined;
  const resolving = !!slug && workspaces.status !== 'ready';

  return (
    <GenericEventsRouteProvider value="tasks">
      <AppShell title="">
        {resolving ? (
          <PageLoader />
        ) : workspace ? (
          <TasksBody
            workspaceId={workspace.id}
            initialAgentId={agentId}
            filterable
            header={
              <PageHeader
                title={t('workspace.agents.title')}
                description={t('workspace.agents.subtitle', { workspace: workspace.name })}
                breadcrumb={[
                  from === 'agents'
                    ? { label: t('nav.yourAgents'), href: '/agents' }
                    : { label: workspace.name, href: `/workspace/${workspace.slug}/agents` },
                  ...(from === 'agents' ? [{ label: workspace.name }] : []),
                  { label: t('workspace.agents.title') },
                ]}
              />
            }
          />
        ) : (
          <TasksBody
            initialAgentId={agentId}
            filterable
            header={
              <PageHeader
                title={t('workspace.agents.title')}
                /* Account-wide list — spans every Space, so the copy names none. */
                description={t('tasks.description.symphony')}
              />
            }
          />
        )}
      </AppShell>
    </GenericEventsRouteProvider>
  );
}
